// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, FormattedMessage } from "react-intl";
import { components, OptionProps } from "react-select";

import { withRouter } from "view/reactrouterinterceptor";
import { ActionType, IAction, IActionTypeSelectIos, raiseAction } from "actions";

import { IState } from "states/states";
import { IInput, InputType, IOutput, OutputMode } from "shared/types/parameter";
import { Aggregation, AggregationType } from "graphql/api";
import { getColor, getIoType, getSiteSelection, toDropdownItems } from "guiutils";
import TrendWindowModel from "model/trendwindow";
import IOsModel from "model/ios";
import {
  ISiteBarDispatch,
  ISiteBarProps,
  SiteBarComponent,
  mapStateToProps as siteBarProps,
  mapDispatchToProps as siteBarDispatch,
} from "./SiteBar";
import Dropdown from "view/comp/Dropdown";
import DropdownSelect, {
  allOption,
  IDropdownSelectOption,
  SELECT_ALL_OPTION_ITEMS,
} from "./DropdownSelect";

import "./SiteBar.scss";

interface IIoSiteBarProps extends ISiteBarProps {
  inputs: IInput[] | undefined;
  outputs: IOutput[] | undefined;
  selectedAggregation: AggregationType;
  selectedIoType: "analog" | "digital";
  aggregation: Aggregation;
  selectedInputs: number[] | undefined;
  selectedOutputs: number[] | undefined;
}

const mapStateToProps = (state: IState) => {
  const siteSelection = getSiteSelection(state);
  const selectedIoType = getIoType(state);
  const aggregation = TrendWindowModel.getIoAggregation(state);

  const props: IIoSiteBarProps = {
    ...siteBarProps(state),
    inputs: state.para.parameter?.input,
    outputs: state.para.parameter?.output,
    selectedAggregation: state.gui.selectedIoAggregationType ?? AggregationType.AVG,
    selectedIoType,
    aggregation,
    selectedInputs: siteSelection?.inputs,
    selectedOutputs: siteSelection?.outputs,
  };

  return props;
};

interface IIoSiteBarDispatch extends ISiteBarDispatch {
  onAggregationDropdownChanged: (value: AggregationType) => void;
  onTypeDropdownChanged: (value: "digital" | "analog") => void;
  onDropdownIOSelectionChanged: (
    type: "digital" | "analog",
    inputIds: number[],
    outputIds: number[],
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => ({
  ...siteBarDispatch(dispatch),
  onAggregationDropdownChanged: (value: AggregationType) => {
    dispatch(
      raiseAction(ActionType.ACT_EDIT_VALUE, {
        name: "selectedIoAggregationType",
        value,
      }),
    );
    dispatch(raiseAction(ActionType.ACT_UPDATE_IOTREND));
  },
  onTypeDropdownChanged: (value: "digital" | "analog") => {
    dispatch(
      raiseAction(ActionType.ACT_EDIT_VALUE, {
        name: "selectedIoType",
        value,
      }),
    );
  },
  onDropdownIOSelectionChanged: (
    type: "digital" | "analog",
    inputIds: number[],
    outputIds: number[],
  ) => {
    const payload: IActionTypeSelectIos = { type, inputIds, outputIds };
    dispatch(raiseAction(ActionType.ACT_SELECT_IOS, payload));
  },
});

const messages = defineMessages({
  typeSelectCaption: {
    id: "Select analog/switch",
  },
  aggregationSelectCaption: {
    id: "Select data aggregation",
  },
  ioSelectionAllSelected: {
    id: "All items selected",
  },
  ioSelectionSomeItemsSelected: {
    id: "items selected",
  },
  inputs: {
    id: "Inputs",
  },
  outputs: {
    id: "Outputs",
  },
});

class IoSiteBarComponent extends SiteBarComponent<IIoSiteBarProps & IIoSiteBarDispatch> {
  public override renderSelectionButtons() {
    const hasDigitalIos =
      this.props.outputs?.some(
        (curItem) => curItem.webActive && curItem.mode !== OutputMode.ANALOG,
      ) ||
      this.props.inputs?.some((curItem) => curItem.webActive && curItem.type !== InputType.ANALOG);
    const hasAnalogIos =
      this.props.outputs?.some(
        (curItem) => curItem.webActive && curItem.mode === OutputMode.ANALOG,
      ) ||
      this.props.inputs?.some((curItem) => curItem.webActive && curItem.type === InputType.ANALOG);

    return (
      <>
        {hasAnalogIos && hasDigitalIos && this.renderTypeSelect()}
        {this.renderIoSelect()}
        {this.props.selectedIoType === "analog" &&
          this.props.aggregation !== Aggregation.NONE &&
          this.renderAggregation()}
      </>
    );
  }

  private renderTypeSelect() {
    const getLabel = (value: "digital" | "analog") =>
      value === "digital" ? <FormattedMessage id="Switches" /> : <FormattedMessage id="Analog" />;
    const items = toDropdownItems(
      ["digital", "analog"],
      "ioType",
      (name, value) => this.props.onTypeDropdownChanged(value as "digital" | "analog"),
      getLabel,
    );

    return (
      <div className="me-2" id="typeselect">
        <Dropdown
          className="m-0"
          style={{ background: "transparent" }}
          isHeaderStyle={true}
          items={items}
          caption={this.context.formatMessage(messages.typeSelectCaption)}
          curItem={getLabel(this.props.selectedIoType)}
        />
      </div>
    );
  }

  private renderAggregation() {
    const getLabel = (value: AggregationType) =>
      value === AggregationType.MIN ? <FormattedMessage id="Minimum" />
      : value === AggregationType.MAX ? <FormattedMessage id="Maximum" />
      : <FormattedMessage id="Average" />;
    const items = toDropdownItems(
      [AggregationType.AVG, AggregationType.MIN, AggregationType.MAX],
      "ioAggregation",
      (name, value) => this.props.onAggregationDropdownChanged(value),
      getLabel,
    );

    return (
      <div className="me-2" id="aggregationselect">
        <Dropdown
          items={items}
          isHeaderStyle={true}
          style={{ background: "transparent" }}
          caption={this.context.formatMessage(messages.aggregationSelectCaption)}
          curItem={getLabel(this.props.selectedAggregation)}
        />
      </div>
    );
  }

  private renderIoSelect() {
    const allOptions = IOsModel.getAllAvailableIos(
      [this.props.inputs ?? [], this.props.outputs ?? []],
      this.props.selectedIoType,
    );
    const groupedOptions = [
      { label: this.context.formatMessage(messages.inputs), options: allOptions[0] },
      { label: this.context.formatMessage(messages.outputs), options: allOptions[1] },
    ];
    const selectedOptions = IOsModel.getSelectedIos(
      allOptions,
      this.props.selectedInputs,
      this.props.selectedOutputs,
      false,
    );
    const selectedOptionsFlat = [...selectedOptions[0], ...selectedOptions[1]];
    const isAllSelected =
      selectedOptions[0].length === allOptions[0].length &&
      selectedOptions[1].length === allOptions[1].length;
    if (isAllSelected) {
      selectedOptionsFlat.push(allOption);
    }

    return (
      <div className="me-2" id="ioselect">
        <DropdownSelect
          options={groupedOptions}
          value={selectedOptionsFlat}
          noOptionsString={<FormattedMessage id="No input/output available" />}
          components={{ Option: (props) => this.optionsCallback(props) }}
          isMulti={true}
          closeMenuOnSelect={false}
          placeholder={
            <div>
              {selectedOptionsFlat.length > 0 ?
                isAllSelected ?
                  this.context.formatMessage(messages.ioSelectionAllSelected)
                : selectedOptionsFlat.length > 1 ?
                  String(selectedOptionsFlat.length) +
                  " " +
                  this.context.formatMessage(messages.ioSelectionSomeItemsSelected)
                : selectedOptionsFlat.length === 1 ?
                  selectedOptionsFlat[0].label
                : <FormattedMessage id="Select input/output" />
              : <FormattedMessage id="No input/output available" />}
            </div>
          }
          onChange={(value: IDropdownSelectOption[]) =>
            this.props.onDropdownIOSelectionChanged(
              this.props.selectedIoType,
              value.filter((curItem) => curItem.source === "input").map((curItem) => curItem.id),
              value.filter((curItem) => curItem.source === "output").map((curItem) => curItem.id),
            )
          }
          allowSelectAll={allOptions.length > 0}
        />
      </div>
    );
  }

  private optionsCallback(props: OptionProps<IDropdownSelectOption, boolean, any>) {
    return (
      <div
        style={{
          backgroundColor: "rgba(0,0,0,0)",
        }}
      >
        <components.Option {...props}>
          <input
            style={{
              accentColor:
                props.data.index === SELECT_ALL_OPTION_ITEMS ?
                  "rgba(1,1,1,1)"
                : getColor(props.data.index),
            }}
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label
            style={{
              color:
                props.data.index === SELECT_ALL_OPTION_ITEMS ?
                  "rgba(1,1,1,1)"
                : getColor(props.data.index),
            }}
          >
            {props.data.index !== SELECT_ALL_OPTION_ITEMS ?
              props.data.label
            : this.context.formatMessage({ id: props.data.label as string })}
          </label>
        </components.Option>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(IoSiteBarComponent));
